import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAmazonProduct, saveAmazonDraft } from "../../api/client";
import type { AmzAttributes, AmzChange, AmzFieldKey, AmzIssue, AmzProductDetail, AmzSource } from "../../types/amazon";
import { baseAttrKey, computeChanges, fieldIndex, getImages, setFieldValue, setImages } from "../../lib/amazonContent";
import { errorMessage } from "./helpers";

export type SaveState = "idle" | "saving" | "saved" | "error";

const SAVE_DEBOUNCE_MS = 600;

export interface ProductEditor {
  detail: AmzProductDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  draft: AmzAttributes;
  sources: Record<AmzFieldKey, AmzSource>;
  changes: AmzChange[];
  saveState: SaveState;
  /** draft を変更する。touched のキーに source を記録し、600msデバウンスで保存する。 */
  edit: (mutator: (draft: AmzAttributes) => AmzAttributes, touched: AmzFieldKey[], source?: AmzSource) => void;
  /** 1フィールドを Amazon 現在値に戻す。 */
  revertField: (fieldKey: AmzFieldKey) => void;
  /** 保留中の保存をすぐ実行して完了を待つ。失敗時 false。 */
  flush: () => Promise<boolean>;
  /** サーバーから受け取った detail で状態を置き換える（同期・反映・破棄の後）。保留中の保存は破棄する。 */
  replaceDetail: (d: AmzProductDetail) => void;
  /** 反映で解消した Issue（この画面セッション内のみ保持）。 */
  resolvedIssues: AmzIssue[];
  markResolved: (issues: AmzIssue[]) => void;
}

export function useProductEditor(sku: string): ProductEditor {
  const [detail, setDetail] = useState<AmzProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [draft, setDraftState] = useState<AmzAttributes>({});
  const [sources, setSourcesState] = useState<Record<AmzFieldKey, AmzSource>>({});
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [resolvedIssues, setResolvedIssues] = useState<AmzIssue[]>([]);

  const draftRef = useRef<AmzAttributes>({});
  const sourcesRef = useRef<Record<AmzFieldKey, AmzSource>>({});
  const detailRef = useRef<AmzProductDetail | null>(null);
  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(true));

  const flush = useCallback((): Promise<boolean> => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    // 直前の保存が終わってから最新の draft を送る（リクエストの追い越しを防ぐ）。
    queueRef.current = queueRef.current.then(async () => {
      if (!dirtyRef.current) return true;
      dirtyRef.current = false;
      setSaveState("saving");
      try {
        await saveAmazonDraft(sku, { draft: draftRef.current, sources: sourcesRef.current });
        if (!dirtyRef.current) setSaveState("saved");
        return true;
      } catch {
        dirtyRef.current = true;
        setSaveState("error");
        return false;
      }
    });
    return queueRef.current;
  }, [sku]);

  const schedule = useCallback(() => {
    dirtyRef.current = true;
    setSaveState("saving");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void flush();
    }, SAVE_DEBOUNCE_MS);
  }, [flush]);

  const applyDetail = useCallback((d: AmzProductDetail) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    dirtyRef.current = false;
    detailRef.current = d;
    draftRef.current = d.draft;
    sourcesRef.current = d.sources;
    setDetail(d);
    setDraftState(d.draft);
    setSourcesState(d.sources);
    setSaveState("idle");
  }, []);

  // 読み込み（sku 変更・再読込）。離脱時は保留中の保存を流す。
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAmazonProduct(sku)
      .then((r) => {
        if (cancelled) return;
        applyDetail(r.detail);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(errorMessage(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
      void flush();
    };
  }, [sku, nonce, applyDetail, flush]);

  // sku が変わったら画面内の一時状態を捨てる。
  useEffect(() => {
    setResolvedIssues([]);
    setDetail(null);
    detailRef.current = null;
  }, [sku]);

  const edit = useCallback(
    (mutator: (d: AmzAttributes) => AmzAttributes, touched: AmzFieldKey[], source: AmzSource = "MANUAL") => {
      const nextDraft = mutator(draftRef.current);
      const nextSources = { ...sourcesRef.current };
      for (const k of touched) nextSources[k] = source;
      draftRef.current = nextDraft;
      sourcesRef.current = nextSources;
      setDraftState(nextDraft);
      setSourcesState(nextSources);
      schedule();
    },
    [schedule]
  );

  const revertField = useCallback(
    (fieldKey: AmzFieldKey) => {
      const d = detailRef.current;
      if (!d) return;
      const cur = d.current ?? {};
      const mp = d.marketplaceId;
      const nextSources = { ...sourcesRef.current };
      delete nextSources[fieldKey];
      let next: AmzAttributes;
      const idx = fieldIndex(fieldKey);
      if (fieldKey === "images") {
        next = setImages(draftRef.current, getImages(cur), mp);
      } else if (idx !== null) {
        const def = d.fields.fields.find((f) => f.key === baseAttrKey(fieldKey));
        const curRow = ((cur[baseAttrKey(fieldKey)] ?? []) as { value?: unknown }[])[idx]?.value;
        next = def ? setFieldValue(draftRef.current, fieldKey, typeof curRow === "string" ? curRow : "", def, mp) : draftRef.current;
      } else {
        next = { ...draftRef.current };
        if (cur[fieldKey]) next[fieldKey] = JSON.parse(JSON.stringify(cur[fieldKey]));
        else delete next[fieldKey];
      }
      draftRef.current = next;
      sourcesRef.current = nextSources;
      setDraftState(next);
      setSourcesState(nextSources);
      schedule();
    },
    [schedule]
  );

  const changes = useMemo(
    () => (detail ? computeChanges(detail.current, draft, detail.fields.fields, sources) : []),
    [detail, draft, sources]
  );

  const markResolved = useCallback((issues: AmzIssue[]) => {
    setResolvedIssues((prev) => [...prev, ...issues]);
  }, []);

  return {
    detail,
    loading,
    error,
    reload: () => setNonce((n) => n + 1),
    draft,
    sources,
    changes,
    saveState,
    edit,
    revertField,
    flush,
    replaceDetail: applyDetail,
    resolvedIssues,
    markResolved,
  };
}
